/**
 * ALT: dot indicators for the mobile full-width PDP gallery (2026-09-02).
 *
 * <alt-gallery-dots> is rendered by sections/product.liquid as a sibling of
 * the [data-product-media-list] scroll container, inside <product-images>,
 * only when the gallery is in mobile-slider mode with more than one image.
 * It has no external dependencies and does no layout of its own beyond the
 * buttons it renders; assets/theme.custom.css owns all positioning/paint.
 */
(function () {
  if (customElements.get('alt-gallery-dots')) return;

  class AltGalleryDots extends HTMLElement {
    connectedCallback() {
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this.init();

      // Guard so a re-render of this section in the theme editor doesn't
      // stack a second document-wide listener on top of the first.
      if (!AltGalleryDots._sectionLoadBound) {
        AltGalleryDots._sectionLoadBound = true;
        document.addEventListener('shopify:section:load', function () {
          document.querySelectorAll('alt-gallery-dots').forEach(function (el) {
            el.init();
          });
        });
      }
    }

    disconnectedCallback() {
      this._teardown();
    }

    init() {
      this._teardown();

      var gallery = this.closest('product-images') || this.closest('.product__images--mobile-slider');
      var container = gallery && gallery.querySelector('[data-product-media-list]');
      var slides = container ? Array.prototype.slice.call(container.querySelectorAll('[data-slide]')) : [];

      if (!gallery || !container || slides.length < 2) {
        this.hidden = true;
        this.innerHTML = '';
        return;
      }

      this.hidden = false;
      this.container = container;
      this.slides = slides;

      var total = slides.length;
      this.innerHTML = slides
        .map(function (slide, i) {
          var active = slide.classList.contains('media--active');
          return '<button type="button" aria-label="Go to image ' + (i + 1) + ' of ' + total + '"' + (active ? ' aria-current="true"' : '') + '></button>';
        })
        .join('');

      this.buttons = Array.prototype.slice.call(this.querySelectorAll('button'));
      this.buttons.forEach(function (button, i) {
        button.addEventListener('click', function () {
          container.scrollTo({left: slides[i].offsetLeft, behavior: 'smooth'});
        });
      });

      var activeIndex = slides.findIndex(function (slide) {
        return slide.classList.contains('media--active');
      });
      if (activeIndex < 0) activeIndex = 0;

      // Deep-linked ?variant= can feature a media that isn't the first
      // slide; jump there instantly (no animation) before the user scrolls.
      if (activeIndex > 0) {
        container.scrollTo({left: slides[activeIndex].offsetLeft, behavior: 'auto'});
      }

      container.addEventListener('scroll', this._onScroll, {passive: true});

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(this._onResize);
        this._resizeObserver.observe(container);
      } else {
        window.addEventListener('resize', this._onResize);
      }

      this._setActive(activeIndex);
    }

    _onScroll() {
      var self = this;
      if (this._raf) return;
      this._raf = requestAnimationFrame(function () {
        self._raf = null;
        var width = self.container.clientWidth || 1;
        var index = Math.round(self.container.scrollLeft / width);
        index = Math.max(0, Math.min(index, self.slides.length - 1));
        self._setActive(index);
      });
    }

    _onResize() {
      this._onScroll();
    }

    _setActive(index) {
      if (!this.buttons) return;
      this.buttons.forEach(function (button, i) {
        if (i === index) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    _teardown() {
      if (this.container) {
        this.container.removeEventListener('scroll', this._onScroll);
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      } else {
        window.removeEventListener('resize', this._onResize);
      }
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }
  }

  customElements.define('alt-gallery-dots', AltGalleryDots);
})();
